package com.rosario.juridico.ui.screens

import com.rosario.juridico.ui.components.CajaResultadoAccionable
import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.core.tween
import androidx.compose.animation.expandVertically
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.shrinkVertically
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.pager.HorizontalPager
import androidx.compose.foundation.pager.rememberPagerState
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import com.rosario.juridico.ui.components.WebScrollableBox
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.material3.MaterialTheme.colorScheme
import androidx.compose.material3.TabRowDefaults.tabIndicatorOffset
import androidx.compose.runtime.*
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.rosario.juridico.domain.*
import com.rosario.juridico.platform.agregarEventoCalendario
import com.rosario.juridico.platform.localDateToMillis
import com.rosario.juridico.ui.theme.Gold400
import com.rosario.juridico.navigation.Routes
import com.rosario.juridico.ui.components.JuridicoDatePickerDialogKMP
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.plus
import kotlinx.datetime.toLocalDateTime
import kotlinx.datetime.Clock
import kotlinx.datetime.Instant
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.style.TextAlign
import androidx.navigation.NavController

// ─────────────────────────────────────────────────────────────────────────────
// PANTALLA PRINCIPAL
// ─────────────────────────────────────────────────────────────────────────────

@Composable
fun PantallaCalculadoraDias(navController: NavController? = null) {
    val pagerState    = rememberPagerState(pageCount = { 3 })
    val scope         = rememberCoroutineScope()

    var feriados by remember { mutableStateOf<Set<LocalDate>>(emptySet()) }
    var dEspeciales by remember { mutableStateOf<List<DiaEspecial>>(emptyList()) }
    
    var fInicio           by remember { mutableStateOf<LocalDate?>(null) }
    var fFin              by remember { mutableStateOf<LocalDate?>(null) }
    var mPickerInicio   by remember { mutableStateOf(false) }
    var mPickerFin      by remember { mutableStateOf(false) }

    LaunchedEffect(Unit) {
        feriados = cargarFeriados()
        dEspeciales = CalendarioParser.cargarTodo()
    }

    Column(modifier = Modifier.fillMaxSize().background(colorScheme.background)) {
        Box(modifier = Modifier.fillMaxWidth().background(colorScheme.surface)) {
            Column {
                TabRow(
                    selectedTabIndex = pagerState.currentPage,
                    containerColor   = colorScheme.surface,
                    contentColor     = colorScheme.primary,
                    indicator        = { tabPositions ->
                        Box(Modifier.tabIndicatorOffset(tabPositions[pagerState.currentPage]).height(3.dp).background(Gold400))
                    },
                    divider = { HorizontalDivider(color = colorScheme.outlineVariant) }
                ) {
                    listOf("Días", "Vencimiento", "Calendario Legal").forEachIndexed { index, titulo ->
                        val sel = pagerState.currentPage == index
                        Tab(
                            selected = sel,
                            onClick  = { scope.launch { pagerState.animateScrollToPage(index) } },
                            text     = { Text(titulo, fontWeight = if (sel) FontWeight.SemiBold else FontWeight.Normal, fontSize = 12.sp) }
                        )
                    }
                }
                UsageCounterIndicator()
            }
        }
        
        HorizontalPager(
            state = pagerState, 
            modifier = Modifier.weight(1f),
            beyondViewportPageCount = 2 
        ) { page ->
            Box(modifier = Modifier.fillMaxSize()) {
                when (page) {
                    0 -> {
                        ContenidoCalcularDias(
                            navController = navController!!,
                            feriados = feriados,
                            fechaInicio = fInicio,
                            fechaFin = fFin,
                            setFechaInicio = { fInicio = it },
                            setFechaFin = { fFin = it },
                            onShowPickerInicio = { mPickerInicio = true },
                            onShowPickerFin = { mPickerFin = true }
                        )
                        OnboardingTooltip(
                            key = "onboarding_calculadora_dias",
                            mensaje = "Calculá rápidamente los días hábiles judiciales entre dos fechas, descontando feriados y fines de semana.",
                            modifier = Modifier.align(Alignment.TopCenter).padding(top = 8.dp)
                        )
                    }
                    1 -> {
                        ContenidoCalcularVencimiento(
                            navController = navController!!,
                            feriados = feriados,
                            fechaIn = fInicio,
                            setFechaIn = { fInicio = it },
                            onShowPickerIn = { mPickerInicio = true }
                        )
                        OnboardingTooltip(
                            key = "onboarding_calculadora_vencimiento",
                            mensaje = "Ingresá un plazo en días y conocé la fecha exacta de vencimiento. Podés agendar una alerta en tu calendario.",
                            modifier = Modifier.align(Alignment.TopCenter).padding(top = 8.dp)
                        )
                    }
                    2 -> {
                        Column(modifier = Modifier.fillMaxSize().verticalScroll(rememberScrollState()).padding(16.dp)) {
                            Card(
                                modifier = Modifier.fillMaxWidth(),
                                shape = RoundedCornerShape(16.dp),
                                colors = CardDefaults.cardColors(
                                    containerColor = if (androidx.compose.foundation.isSystemInDarkTheme()) 
                                        colorScheme.surfaceVariant 
                                    else Color(0xFF1A4B8C)
                                ),
                                elevation = CardDefaults.cardElevation(4.dp)
                            ) {
                                Column(modifier = Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(12.dp)) {
                                    Row(verticalAlignment = Alignment.CenterVertically) {
                                        Icon(Icons.Default.CalendarToday, null, tint = Color(0xFFFFD700), modifier = Modifier.size(20.dp))
                                        Spacer(Modifier.width(8.dp))
                                        Text("Plazos Judiciales", style = MaterialTheme.typography.titleSmall, fontWeight = FontWeight.Bold, color = Color.White)
                                    }
                                    Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                        Button(
                                            onClick = { scope.launch { pagerState.animateScrollToPage(0) } },
                                            modifier = Modifier.weight(1f),
                                            colors = ButtonDefaults.buttonColors(containerColor = Color.White, contentColor = Color(0xFF1A4B8C)),
                                            shape = RoundedCornerShape(10.dp),
                                            contentPadding = PaddingValues(0.dp)
                                        ) {
                                            Text("Contador de Días", fontSize = 12.sp, fontWeight = FontWeight.Bold)
                                        }
                                        Button(
                                            onClick = { scope.launch { pagerState.animateScrollToPage(1) } },
                                            modifier = Modifier.weight(1f),
                                            colors = ButtonDefaults.buttonColors(containerColor = Color.White, contentColor = Color(0xFF1A4B8C)),
                                            shape = RoundedCornerShape(10.dp),
                                            contentPadding = PaddingValues(0.dp)
                                        ) {
                                            Text("Caducidad", fontSize = 12.sp, fontWeight = FontWeight.Bold)
                                        }
                                    }
                                }
                            }
                            Spacer(Modifier.height(16.dp))
                            CalendarioJudicialComponent(diasEspeciales = dEspeciales)
                            Spacer(Modifier.height(100.dp)) 
                        }
                    }
                }
            }
        }
    }

    if (mPickerInicio) {
        JuridicoDatePickerDialogKMP(
            onDateSelected = { 
                fInicio = it
                mPickerInicio = false 
            }, 
            onDismiss = { mPickerInicio = false }
        )
    }
    if (mPickerFin) {
        JuridicoDatePickerDialogKMP(
            onDateSelected = { 
                fFin = it
                mPickerFin = false 
            }, 
            onDismiss = { mPickerFin = false }
        )
    }
}

@Composable
fun ContenidoCalcularDias(
    navController: NavController,
    feriados: Set<LocalDate>,
    fechaInicio: LocalDate?,
    fechaFin: LocalDate?,
    setFechaInicio: (LocalDate?) -> Unit,
    setFechaFin: (LocalDate?) -> Unit,
    onShowPickerInicio: () -> Unit,
    onShowPickerFin: () -> Unit
) {
    var diasResultado         by remember { mutableStateOf<Int?>(null) }
    var dCorrResultado by remember { mutableStateOf<Int?>(null) }
    var resultVisible by remember { mutableStateOf(false) }
    val scrollState = rememberScrollState()

    LaunchedEffect(diasResultado, dCorrResultado) {
        if (diasResultado != null || dCorrResultado != null) {
            delay(400)
            resultVisible = true
            delay(3000)
            scrollState.animateScrollTo(scrollState.maxValue)
        } else {
            resultVisible = false
        }
    }

    WebScrollableBox(scrollState = scrollState, modifier = Modifier.fillMaxSize()) {
        Column(
            modifier            = Modifier.fillMaxSize().imePadding().verticalScroll(scrollState).padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(14.dp)
        ) {
        PremiumHeader(icono = Icons.Default.CalendarMonth, titulo = "Días Hábiles", subtitulo = "Calculá los días hábiles judiciales entre dos fechas")
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(12.dp)) {
            FechaSelector("Fecha inicio", fechaInicio?.toTexto(), Modifier.weight(1f), onClick = onShowPickerInicio)
            FechaSelector("Fecha fin",    fechaFin?.toTexto(), Modifier.weight(1f), onClick = onShowPickerFin)
        }
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            OutlinedButton(
                onClick  = { setFechaInicio(null); setFechaFin(null); diasResultado = null; dCorrResultado = null },
                modifier = Modifier.weight(0.4f).height(52.dp),
                shape    = RoundedCornerShape(12.dp),
                colors   = ButtonDefaults.outlinedButtonColors(contentColor = colorScheme.error)
            ) {
                Icon(Icons.Default.DeleteSweep, null, Modifier.size(18.dp))
                Spacer(Modifier.width(4.dp))
                Text("Limpiar")
            }
            CalculatorActionLock(
                navController = navController,
                onAction = {
                    if (fechaInicio != null && fechaFin != null) {
                        if (fechaInicio > fechaFin) {
                            diasResultado = null
                        } else {
                            diasResultado = calcularDiasHabilesJudiciales(fechaInicio, fechaFin, feriados)
                        }
                    }
                }
            ) { onClick ->
                Button(
                    onClick = onClick,
                    enabled  = fechaInicio != null && fechaFin != null,
                    modifier = Modifier.weight(0.6f).height(52.dp),
                    shape    = RoundedCornerShape(12.dp)
                ) {
                    Icon(Icons.Default.CalendarMonth, null, Modifier.size(18.dp))
                    Spacer(Modifier.width(8.dp))
                    Text("Hábiles", fontWeight = FontWeight.SemiBold)
                }
            }
        }
        AnimatedVisibility(visible = resultVisible && diasResultado != null) {
            diasResultado?.let { dias ->
                val textoCopia = """
                    CÓMPUTO DE DÍAS HÁBILES JUDICIALES
                    Desde: ${fechaInicio?.toTexto()}
                    Hasta: ${fechaFin?.toTexto()}
                    -------------------------------------------
                    RESULTADO: $dias días hábiles judiciales
                    -------------------------------------------
                    Calculado con www.juridicorosario.com
                """.trimIndent()

                CajaResultadoAccionable(
                    titulo = "Resultado Días Hábiles",
                    textoParaCompartir = textoCopia,
                    colorFondo = colorScheme.primaryContainer
                ) {
                    Column(modifier = Modifier.fillMaxWidth(), horizontalAlignment = Alignment.CenterHorizontally) {
                        Icon(Icons.Default.CheckCircle, null, tint = colorScheme.primary, modifier = Modifier.size(32.dp))
                        Spacer(Modifier.height(8.dp))
                        Text("$dias", style = MaterialTheme.typography.headlineLarge, fontWeight = FontWeight.Bold, color = colorScheme.primary)
                        Text("días hábiles judiciales", style = MaterialTheme.typography.bodyMedium, color = colorScheme.onPrimaryContainer)
                    }
                }
            }
        }
        HorizontalDivider(color = colorScheme.outlineVariant)
        Text("Días Corridos", style = MaterialTheme.typography.titleSmall, fontWeight = FontWeight.SemiBold, color = colorScheme.secondary)
        CalculatorActionLock(
            navController = navController,
            onAction = {
                if (fechaInicio != null && fechaFin != null) {
                    dCorrResultado = if (fechaInicio!! > fechaFin!!) null
                    else calcularDiasCorridos(fechaInicio, fechaFin)
                }
            }
        ) { onClick ->
            Button(
                onClick = onClick,
                enabled  = fechaInicio != null && fechaFin != null,
                modifier = Modifier.fillMaxWidth().height(52.dp),
                shape    = RoundedCornerShape(12.dp),
                colors   = ButtonDefaults.buttonColors(containerColor = colorScheme.secondary)
            ) {
                Icon(Icons.Default.DateRange, null, Modifier.size(18.dp))
                Spacer(Modifier.width(8.dp))
                Text("Calcular días corridos", fontWeight = FontWeight.SemiBold)
            }
        }
        AnimatedVisibility(visible = resultVisible && dCorrResultado != null) {
            dCorrResultado?.let { dias ->
                val textoCopia = """
                    CÓMPUTO DE DÍAS CORRIDOS
                    Desde: ${fechaInicio?.toTexto()}
                    Hasta: ${fechaFin?.toTexto()}
                    -------------------------------------------
                    RESULTADO: $dias días corridos
                    -------------------------------------------
                    Calculado con www.juridicorosario.com
                """.trimIndent()

                CajaResultadoAccionable(
                    titulo = "Resultado Días Corridos",
                    textoParaCompartir = textoCopia,
                    colorFondo = colorScheme.secondaryContainer
                ) {
                    Column(modifier = Modifier.padding(20.dp).fillMaxWidth(), horizontalAlignment = Alignment.CenterHorizontally) {
                        Icon(Icons.Default.CheckCircle, null, tint = colorScheme.secondary, modifier = Modifier.size(32.dp))
                        Spacer(Modifier.height(8.dp))
                        Text("$dias", style = MaterialTheme.typography.headlineLarge, fontWeight = FontWeight.Bold, color = colorScheme.secondary)
                        Text("días corridos", style = MaterialTheme.typography.bodyMedium, color = colorScheme.onSecondaryContainer)
                    }
                }
            }
        }
        Spacer(Modifier.height(50.dp))
    }
}
}

@Composable
fun ContenidoCalcularVencimiento(
    navController: NavController,
    feriados: Set<LocalDate>,
    fechaIn: LocalDate?,
    setFechaIn: (LocalDate?) -> Unit,
    onShowPickerIn: () -> Unit
) {
    var fVencimiento     by remember { mutableStateOf<LocalDate?>(null) }
    var dHabilesStr       by remember { mutableStateOf("") }
    var dCorridosStr      by remember { mutableStateOf("") }
    var descAlerta    by remember { mutableStateOf("") }
    var res            by remember { mutableStateOf("") }
    var mDialogoCalendario by remember { mutableStateOf(false) }
    var resultVisible by remember { mutableStateOf(false) }
    val scrollState = rememberScrollState()

    LaunchedEffect(fVencimiento) {
        if (fVencimiento != null) {
            delay(400)
            resultVisible = true
            delay(100)
            scrollState.animateScrollTo(scrollState.maxValue)
        } else {
            resultVisible = false
        }
    }

    Column(
        modifier            = Modifier.fillMaxSize().imePadding().verticalScroll(scrollState).padding(16.dp),
        verticalArrangement = Arrangement.spacedBy(14.dp)
    ) {
        PremiumHeader(icono = Icons.Default.DateRange, titulo = "Fecha de Vencimiento", subtitulo = "Calculá cuándo vence un plazo legal")
        FechaSelector("Fecha de inicio del plazo", fechaIn?.toTexto(), Modifier.fillMaxWidth(), onClick = onShowPickerIn)
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(12.dp)) {
            OutlinedTextField(value = dHabilesStr, onValueChange = { if (it.all { c -> c.isDigit() }) { dHabilesStr = it; if (it.isNotEmpty()) dCorridosStr = "" } }, label = { Text("Días hábiles") }, singleLine = true, enabled = dCorridosStr.isEmpty(), modifier = Modifier.weight(1f), shape = RoundedCornerShape(12.dp))
            OutlinedTextField(value = dCorridosStr, onValueChange = { if (it.all { c -> c.isDigit() }) { dCorridosStr = it; if (it.isNotEmpty()) dHabilesStr = "" } }, label = { Text("Días corridos") }, singleLine = true, enabled = dHabilesStr.isEmpty(), modifier = Modifier.weight(1f), shape = RoundedCornerShape(12.dp))
        }
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            OutlinedButton(onClick = { setFechaIn(null); fVencimiento = null; dHabilesStr = ""; dCorridosStr = ""; res = "" }, modifier = Modifier.weight(0.4f).height(52.dp), shape = RoundedCornerShape(12.dp), colors = ButtonDefaults.outlinedButtonColors(contentColor = colorScheme.error)) {
                Icon(Icons.Default.DeleteSweep, null, Modifier.size(18.dp))
                Spacer(Modifier.width(4.dp))
                Text("Limpiar")
            }
            CalculatorActionLock(
                navController = navController,
                onAction = {
                    if (fechaIn == null) { res = "Seleccioná la fecha de inicio."; return@CalculatorActionLock }
                    val dh = dHabilesStr.toIntOrNull()
                    val dc = dCorridosStr.toIntOrNull()
                    when {
                        dh != null && dh > 0 -> { 
                            val fv = calcularFechaFin(fechaIn, dh, feriados)
                            fVencimiento = fv
                            res = fv.toTexto()
                        }
                        dc != null && dc > 0  -> { 
                            val fv = fechaIn.plus(dc, DateTimeUnit.DAY)
                            fVencimiento = fv
                            res = fv.toTexto()
                        }
                        else                  -> { fVencimiento = null; res = "Ingresá días hábiles o corridos." }
                    }
                }
            ) { onClick ->
                Button(onClick = onClick, enabled  = fechaIn != null && (dHabilesStr.isNotBlank() || dCorridosStr.isNotBlank()), modifier = Modifier.weight(0.6f).height(52.dp), shape = RoundedCornerShape(12.dp)) {
                    Icon(Icons.Default.DateRange, null, Modifier.size(18.dp))
                    Spacer(Modifier.width(8.dp))
                    Text("Calcular", fontWeight = FontWeight.SemiBold)
                }
            }
        }
        AnimatedVisibility(visible = resultVisible && fVencimiento != null) {
            fVencimiento?.let {
                val textoCopia = """
                    FECHA DE VENCIMIENTO LEGAL
                    Inicio del plazo: ${fechaIn?.toTexto()}
                    Plazo: ${if (dHabilesStr.isNotBlank()) "$dHabilesStr días hábiles" else "$dCorridosStr días corridos"}
                    -------------------------------------------
                    VENCIMIENTO: $res
                    -------------------------------------------
                    Calculado con www.juridicorosario.com
                """.trimIndent()

                CajaResultadoAccionable(
                    titulo = "Resultado Vencimiento",
                    textoParaCompartir = textoCopia,
                    colorFondo = colorScheme.primaryContainer
                ) {
                    Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                        Column(modifier = Modifier.fillMaxWidth(), horizontalAlignment = Alignment.CenterHorizontally) {
                            Text("El plazo vence el", style = MaterialTheme.typography.bodyMedium, color = colorScheme.onPrimaryContainer)
                            Text(res, style = MaterialTheme.typography.headlineMedium, fontWeight = FontWeight.Bold, color = colorScheme.primary)
                        }
                        OutlinedTextField(value = descAlerta, onValueChange = { descAlerta = it }, label = { Text("Descripción del recordatorio (opcional)") }, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(10.dp))
                        Button(onClick = { mDialogoCalendario = true }, modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(10.dp)) { Icon(Icons.Default.DateRange, null, Modifier.size(16.dp)); Spacer(Modifier.width(8.dp)); Text("Agregar alerta en Calendario") }
                    }
                }
            }
        }
        Spacer(Modifier.height(50.dp))
    }

    if (mDialogoCalendario && fVencimiento != null) {
        val t = "Vencimiento Legal: $res"
        val d   = "Calculado por Jurídico Rosario\nInicio: ${fechaIn?.toTexto() ?: "-"}\n"
        AlertDialog(
            onDismissRequest = { mDialogoCalendario = false },
            title   = { Text("Agregar al Calendario") },
            text    = { Text("Se creará un evento para el $res.") },
            confirmButton = { Button(onClick = { mDialogoCalendario = false; agregarEventoCalendario(t, localDateToMillis(fVencimiento!!), d) }) { Text("Aceptar") } },
            dismissButton = { TextButton(onClick = { mDialogoCalendario = false }) { Text("Cancelar") } }
        )
    }
}

@Composable
fun FechaSelector(label: String, valor: String?, modifier: Modifier = Modifier, onClick: () -> Unit) {
    OutlinedButton(onClick = onClick, modifier = modifier.height(56.dp), shape = RoundedCornerShape(12.dp)) {
        Icon(Icons.Default.CalendarMonth, null, modifier = Modifier.size(16.dp))
        Spacer(Modifier.width(8.dp))
        Column(horizontalAlignment = Alignment.Start) {
            Text(label, style = MaterialTheme.typography.labelSmall)
            Text(valor ?: "Seleccionar", style = MaterialTheme.typography.bodyMedium)
        }
    }
}

@Composable
fun PremiumHeader(icono: ImageVector, titulo: String, subtitulo: String) {
    Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.fillMaxWidth()) {
        Icon(icono, null, tint = colorScheme.primary, modifier = Modifier.size(28.dp))
        Spacer(Modifier.width(12.dp))
        Column {
            Text(titulo, style = MaterialTheme.typography.titleLarge, fontWeight = FontWeight.Bold, color = colorScheme.primary)
            Text(subtitulo, style = MaterialTheme.typography.bodySmall, color = colorScheme.onSurfaceVariant)
        }
    }
}
